<ng-container *ngIf="vm$ | async as vm">
  <div #win class="chat-window" *ngIf="vm.ui.open && vm.enabled !== false" role="dialog" aria-label="Chat"
    [class.minimized]="vm.ui.minimized" [class.resizing]="!!resize" [attr.data-tone]="tone(vm.conversation)"
    [style.right.px]="right" [style.bottom.px]="vm.ui.minimized ? bottom : shown.bottom" [style.width.px]="width"
    [style.height.px]="vm.ui.minimized ? null : shown.height"
    [style.--keyboard-top.px]="visible.top" [style.--keyboard-cover.px]="keyboardCover">

    <!-- Resize handles along every edge and at every corner. -->
    <ng-container *ngIf="!vm.ui.minimized">
      <div *ngFor="let edge of edges" class="resize-handle" [ngClass]="edge" (pointerdown)="startResize($event, edge)"
        aria-hidden="true"></div>
    </ng-container>

    <div class="header" (pointerdown)="startDrag($event, win)" (click)="headerClick()">
      <button mat-icon-button *ngIf="vm.conversation && !twoPane" (click)="ui.showList(); $event.stopPropagation()" aria-label="Back to conversations">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <mat-icon class="kind" *ngIf="!vm.conversation">forum</mat-icon>
      <ng-container *ngIf="vm.conversation?.type === 'channel'">
        <img class="kind-photo" *ngIf="photoOf(vm.conversation) as src; else channelIcon" [src]="src" alt="" />
        <ng-template #channelIcon><mat-icon class="kind">{{ vm.conversation?.visibility === 'private' ? 'lock' : 'groups' }}</mat-icon></ng-template>
      </ng-container>
      <mat-icon class="kind" *ngIf="vm.conversation?.type === 'group_dm'">group</mat-icon>
      <mat-icon class="kind" *ngIf="vm.conversation?.type === 'patient'" matTooltip="Patient chat: everyone in the practice can read it">personal_injury</mat-icon>
      <mat-icon class="kind" *ngIf="vm.conversation?.type === 'direct'">person</mat-icon>
      <mat-icon class="kind" *ngIf="vm.conversation?.type === 'network'" matTooltip="A colleague outside your practice">medical_services</mat-icon>
      <mat-icon class="kind" *ngIf="vm.conversation?.type === 'referral'" matTooltip="Referral chat with another practice">swap_horiz</mat-icon>
      <mat-icon class="kind" *ngIf="vm.conversation?.type === 'patient_direct'" matTooltip="The patient, from the patient app">phone_iphone</mat-icon>
      <mat-icon class="kind" *ngIf="vm.conversation?.type === 'support'">support_agent</mat-icon>
      <!-- With a conversation open the name is a button, as in WhatsApp: who is here slides down under it. -->
      <button type="button" class="title who" *ngIf="vm.conversation && !vm.ui.minimized; else plainTitle"
        (click)="toggleMembers(vm.conversation, $event)" [attr.aria-expanded]="membersOpen" aria-controls="chat-members">
        <span class="name">{{ vm.title }}</span>
        <span class="status" *ngIf="vm.status !== 'ready'; else whoLine">
          {{ vm.status === 'reconnecting' ? 'Reconnecting…' : vm.status === 'unavailable' ? 'Unavailable' : 'Connecting…' }}
        </span>
        <ng-template #whoLine><span class="subtitle">{{ subtitle(vm.conversation) }}</span></ng-template>
      </button>
      <ng-template #plainTitle>
        <div class="title">
          <span class="name">{{ vm.title }}</span>
          <span class="status" *ngIf="vm.status !== 'ready'">
            {{ vm.status === 'reconnecting' ? 'Reconnecting…' : vm.status === 'unavailable' ? 'Unavailable' : 'Connecting…' }}
          </span>
        </div>
      </ng-template>
      <mat-icon class="muted-mark" *ngIf="vm.conversation && muted(vm.conversation)" matTooltip="Muted">notifications_off</mat-icon>
      <span class="unread-badge" *ngIf="vm.ui.minimized && vm.badge" [attr.aria-label]="vm.badge + ' unread'">{{ vm.badge }}</span>
      <!-- The patient's file, one tap from any conversation about them (patient, referral, patient-app chat). -->
      <button mat-icon-button *ngIf="!vm.ui.minimized && fileOf(vm.conversation) as ref" (click)="openFile(ref); $event.stopPropagation()"
        matTooltip="Open patient file" aria-label="Open patient file"><mat-icon>visibility</mat-icon></button>
      <!-- The conversation's menu (as WhatsApp's): details, which messages notify you, and mute. -->
      <button mat-icon-button *ngIf="vm.conversation && !vm.ui.minimized" [matMenuTriggerFor]="convMenu.menu" (click)="$event.stopPropagation()"
        (menuOpened)="menuAccent = toneOf(win)" matTooltip="Details, notifications and mute" aria-label="Conversation menu"><mat-icon>more_vert</mat-icon></button>
      <app-chat-conversation-menu #convMenu [conversation]="vm.conversation" [accent]="menuAccent"></app-chat-conversation-menu>
      <button mat-icon-button (click)="ui.setMinimized(!vm.ui.minimized); $event.stopPropagation()"
        [matTooltip]="vm.ui.minimized ? 'Restore' : 'Minimise'" [attr.aria-label]="vm.ui.minimized ? 'Restore' : 'Minimise'">
        <mat-icon>{{ vm.ui.minimized ? 'expand_less' : 'remove' }}</mat-icon>
      </button>
      <button mat-icon-button (click)="ui.close(); $event.stopPropagation()" matTooltip="Close" aria-label="Close chat"><mat-icon>close</mat-icon></button>
    </div>

    <div class="body" *ngIf="!vm.ui.minimized" [class.two-pane]="twoPane">
      <!--
        A panel inside the window (new conversation, details, notifications, receipts, ...): slides up over the body, in
        the window's own colours, instead of a dialog over the whole page. ChatSheetService decides what shows.
      -->
      <!-- A yes/no question (delete, leave, link to the record, ...): a small card above everything in the window. -->
      <div class="confirm-layer" *ngIf="sheets.confirm$ | async as q">
        <div class="confirm-scrim" (click)="sheets.answer(false)"></div>
        <section class="chat-confirm" role="alertdialog" aria-modal="true" aria-labelledby="chat-confirm-title"
          aria-describedby="chat-confirm-message" cdkTrapFocus [cdkTrapFocusAutoCapture]="true">
          <h2 id="chat-confirm-title">{{ q.title }}</h2>
          <p id="chat-confirm-message">{{ q.message }}</p>
          <div class="confirm-choices" *ngIf="q.choices?.length">
            <mat-checkbox class="all" color="primary" [checked]="sheets.allPicked" [indeterminate]="sheets.pickedCount > 0 && !sheets.allPicked" (change)="sheets.toggleAllPicks()">Select all</mat-checkbox>
            <mat-checkbox color="primary" *ngFor="let c of q.choices; let i = index" [checked]="sheets.isPicked(i)" (change)="sheets.togglePick(i)">{{ c }}</mat-checkbox>
          </div>
          <div class="confirm-actions">
            <button mat-button type="button" class="confirm-cancel" (click)="sheets.answer(false)">{{ q.cancelText || 'Cancel' }}</button>
            <button mat-stroked-button type="button" class="confirm-second" *ngIf="q.secondText" (click)="sheets.answerSecond()">{{ q.secondText }}</button>
            <button mat-flat-button type="button" class="confirm-go" [class.danger]="q.danger" (click)="sheets.answer(true)" [disabled]="!!q.choices?.length && !sheets.pickedCount" cdkFocusInitial>{{ q.confirmText }}</button>
          </div>
        </section>
      </div>
      <div class="sheet-layer" *ngIf="sheet && sheetInjector">
        <div class="sheet-scrim" (click)="sheet.ref.close()"></div>
        <section class="chat-sheet" role="dialog" aria-modal="true" cdkTrapFocus [cdkTrapFocusAutoCapture]="true">
          <button mat-icon-button class="sheet-close" type="button" (click)="sheet.ref.close()" aria-label="Close"><mat-icon>close</mat-icon></button>
          <ng-container *ngIf="sheetTemplate(sheet) as tpl; else sheetComp">
            <ng-container *ngTemplateOutlet="tpl; injector: sheetInjector"></ng-container>
          </ng-container>
          <ng-template #sheetComp>
            <ng-container *ngComponentOutlet="sheetComponent(sheet); injector: sheetInjector"></ng-container>
          </ng-template>
        </section>
      </div>
      <!-- Wide window: the list stays on the left and the conversation opens beside it. Narrow: one pane at a time. -->
      <aside class="side" *ngIf="twoPane">
        <app-chat-conversation-list></app-chat-conversation-list>
      </aside>
      <div class="main" #stage>
        <!-- Who is in the open conversation, slid down from under the header over the messages. -->
        <section id="chat-members" class="members-panel" *ngIf="vm.conversation" [class.open]="membersOpen" [attr.aria-hidden]="!membersOpen">
          <div class="members-head">
            <span>{{ members ? members.length + (members.length === 1 ? ' person' : ' people') + ' in this conversation' : 'Loading who is here…' }}</span>
            <button mat-button type="button" (click)="membersOpen = false; info(vm.conversation)" [tabindex]="membersOpen ? 0 : -1">Manage</button>
          </div>
          <div class="members-list">
            <div class="loading" *ngIf="!members"><mat-spinner diameter="22"></mat-spinner></div>
            <div class="member" *ngFor="let m of members">
              <span class="face" [style.--hue]="faceHue(m)">
                <img *ngIf="facePhoto(m) as src; else letters" [src]="src" alt="" loading="lazy" />
                <ng-template #letters>{{ faceInitials(m) }}</ng-template>
                <span class="presence" [class.on]="m.online" *ngIf="!isMe(m)"></span>
              </span>
              <span class="member-name">{{ isMe(m) ? 'You' : m.name }}</span>
              <span class="role" *ngIf="m.role && m.role !== 'member'">{{ m.role }}</span>
            </div>
          </div>
        </section>
        <div class="members-scrim" *ngIf="vm.conversation && membersOpen" (click)="membersOpen = false"></div>
        <app-chat-thread *ngIf="vm.conversation; else list" [conversation]="vm.conversation" [isOrgAdmin]="vm.isOrgAdmin" [embedded]="true"></app-chat-thread>
        <ng-template #list>
          <div class="missing" *ngIf="vm.ui.conversationId && !vm.conversation">
            This conversation is not available. <button mat-button (click)="ui.showList()">Back</button>
          </div>
          <div class="nothing-open" *ngIf="twoPane && !vm.ui.conversationId">
            <mat-icon>forum</mat-icon>
            <p>Pick a conversation on the left, or start a new one.</p>
          </div>
          <app-chat-conversation-list *ngIf="!twoPane && !vm.ui.conversationId"></app-chat-conversation-list>
        </ng-template>
      </div>
    </div>
  </div>
</ng-container>
